import { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  Icon,
  SimpleGrid,
  Text
} from '@chakra-ui/react'
import {
  ArrowRightIcon,
  ArrowUpRightIcon,
  CalendarDaysIcon,
  ChartBarIcon,
  CheckIcon,
  ClipboardDocumentCheckIcon,
  FolderOpenIcon,
  MagnifyingGlassIcon,
  QueueListIcon,
  SparklesIcon
} from '@heroicons/react/24/outline'
import { useIntl, type MessageDescriptor } from 'react-intl'
import { trackEvent } from '../../services/analytics'
import LandingNavbar from '../../components/LandingNavbar/LandingNavbar'
import messages from './messages'

const DEMO_URL = 'https://calendar.notion.so/meet/mostafaelgayar/l72vw4ohd'

interface FeatureDefinition {
  icon: typeof FolderOpenIcon
  title: MessageDescriptor
  description: MessageDescriptor
}

const FEATURES: FeatureDefinition[] = [
  {
    icon: FolderOpenIcon,
    title: messages.patientRecordsTitle,
    description: messages.patientRecordsDescription
  },
  {
    icon: QueueListIcon,
    title: messages.queueTitle,
    description: messages.queueDescription
  },
  {
    icon: ClipboardDocumentCheckIcon,
    title: messages.visitsTitle,
    description: messages.visitsDescription
  },
  {
    icon: CalendarDaysIcon,
    title: messages.schedulingTitle,
    description: messages.schedulingDescription
  },
  {
    icon: SparklesIcon,
    title: messages.clinicalToolsTitle,
    description: messages.clinicalToolsDescription
  },
  {
    icon: ChartBarIcon,
    title: messages.growthTitle,
    description: messages.growthDescription
  }
]

function ProductPreview() {
  const { formatMessage } = useIntl()

  return (
    <Box
      position="relative"
      border="1px solid"
      borderColor="gray.300"
      borderRadius={{ base: '12px', md: '16px' }}
      bg="white"
      boxShadow="0 30px 80px rgba(17, 24, 39, 0.13)"
      overflow="hidden"
      aria-label="Sijilo dashboard preview"
    >
      <Flex
        h="44px"
        align="center"
        gap={3}
        px={3}
        borderBottom="1px solid"
        borderColor="gray.200"
      >
        <Flex gap={1.5} flexShrink={0}>
          {[0, 1, 2].map((dot) => (
            <Box key={dot} w="7px" h="7px" borderRadius="full" bg="gray.300" />
          ))}
        </Flex>
        <Flex
          mx="auto"
          w="48%"
          h="27px"
          align="center"
          gap={2}
          px={2.5}
          border="1px solid"
          borderColor="gray.200"
          borderRadius="7px"
          color="gray.400"
        >
          <MagnifyingGlassIcon width="13px" />
          <Text fontSize="9px" noOfLines={1}>
            {formatMessage(messages.previewSearch)}
          </Text>
        </Flex>
        <Box w="24px" h="24px" borderRadius="7px" bg="gray.900" />
      </Flex>

      <Flex minH={{ base: '320px', md: '390px' }}>
        <Flex
          direction="column"
          align="center"
          gap={2}
          w={{ base: '42px', md: '52px' }}
          py={3}
          bg="gray.50"
          borderInlineEnd="1px solid"
          borderColor="gray.200"
        >
          {[FolderOpenIcon, CalendarDaysIcon, ChartBarIcon].map(
            (SidebarIcon, index) => (
              <Flex
                key={index}
                w={{ base: '28px', md: '32px' }}
                h={{ base: '28px', md: '32px' }}
                align="center"
                justify="center"
                borderRadius="7px"
                bg={index === 0 ? 'gray.900' : 'transparent'}
                color={index === 0 ? 'white' : 'gray.500'}
              >
                <SidebarIcon width="15px" />
              </Flex>
            )
          )}
          <Box mt="auto">
            {['MA', 'OH', 'SN'].map((initials, index) => (
              <Flex
                key={initials}
                mt={index === 0 ? 0 : '-6px'}
                w="25px"
                h="25px"
                align="center"
                justify="center"
                borderRadius="full"
                border="2px solid"
                borderColor="gray.50"
                bg="gray.200"
                color="gray.700"
                fontSize="7px"
                fontWeight="700"
              >
                {initials}
              </Flex>
            ))}
          </Box>
        </Flex>

        <Box flex={1} minW={0} p={{ base: 3, md: 5 }} bg="#fbfbfb">
          <Flex justify="space-between" align="center" mb={4}>
            <Box>
              <Text color="gray.500" fontSize="9px" fontWeight="700">
                {formatMessage(messages.previewToday)}
              </Text>
              <Text
                color="gray.900"
                fontSize={{ base: 'sm', md: 'md' }}
                fontWeight="700"
              >
                {formatMessage(messages.previewDate)}
              </Text>
            </Box>
            <Button
              size="xs"
              h="27px"
              px={2.5}
              bg="gray.900"
              color="white"
              borderRadius="7px"
              fontSize="9px"
            >
              + {formatMessage(messages.previewVisits)}
            </Button>
          </Flex>

          <SimpleGrid columns={3} gap={2} mb={3}>
            {[
              [formatMessage(messages.previewPatients), '128'],
              [formatMessage(messages.previewVisits), '24'],
              [formatMessage(messages.previewWaiting), '08']
            ].map(([label, value]) => (
              <Box
                key={label}
                p={{ base: 2, md: 3 }}
                border="1px solid"
                borderColor="gray.200"
                borderRadius="9px"
                bg="white"
              >
                <Text color="gray.500" fontSize="8px" noOfLines={1}>
                  {label}
                </Text>
                <Text
                  mt={1}
                  color="gray.900"
                  fontSize={{ base: 'md', md: 'xl' }}
                  fontWeight="750"
                >
                  {value}
                </Text>
              </Box>
            ))}
          </SimpleGrid>

          <SimpleGrid columns={{ base: 1, md: 5 }} gap={2}>
            <Box
              gridColumn={{ md: 'span 3' }}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="9px"
              bg="white"
              overflow="hidden"
            >
              <Flex
                align="center"
                justify="space-between"
                px={3}
                py={2.5}
                borderBottom="1px solid"
                borderColor="gray.100"
              >
                <Text fontSize="9px" fontWeight="700">
                  {formatMessage(messages.previewQueue)}
                </Text>
                <Badge bg="gray.100" color="gray.600" fontSize="7px">
                  3 {formatMessage(messages.previewWaiting)}
                </Badge>
              </Flex>
              {['Mariam A.', 'Omar H.', 'Salma N.'].map((name, index) => (
                <Flex
                  key={name}
                  align="center"
                  gap={2}
                  px={3}
                  py={2}
                  borderBottom={index < 2 ? '1px solid' : 'none'}
                  borderColor="gray.100"
                >
                  <Flex
                    w="22px"
                    h="22px"
                    align="center"
                    justify="center"
                    borderRadius="7px"
                    bg="gray.100"
                    color="gray.500"
                    fontSize="8px"
                    fontWeight="700"
                  >
                    {index + 1}
                  </Flex>
                  <Text flex={1} fontSize="9px" fontWeight="600">
                    {name}
                  </Text>
                  <Text color="gray.400" fontSize="8px">
                    10:{20 + index * 5}
                  </Text>
                </Flex>
              ))}
            </Box>

            <Box
              display={{ base: 'none', md: 'block' }}
              gridColumn="span 2"
              p={3}
              border="1px solid"
              borderColor="gray.200"
              borderRadius="9px"
              bg="white"
            >
              <Text fontSize="9px" fontWeight="700" mb={3}>
                {formatMessage(messages.previewFollowUp)}
              </Text>
              {[72, 46, 61, 34, 54, 42, 67].map((height, index) => (
                <Box
                  key={index}
                  display="inline-block"
                  verticalAlign="bottom"
                  w="8%"
                  h={`${height}px`}
                  mx="3%"
                  borderRadius="3px 3px 0 0"
                  bg={index === 6 ? 'gray.900' : 'gray.200'}
                />
              ))}
              <Flex mt={3} align="center" gap={1.5} color="gray.500">
                <CheckIcon width="12px" />
                <Text fontSize="8px">
                  {formatMessage(messages.previewCompleted)}
                </Text>
              </Flex>
            </Box>
          </SimpleGrid>
        </Box>
      </Flex>
    </Box>
  )
}

const structuredData = {
  '@context': 'https://schema.org',
  '@graph': [
    {
      '@type': 'WebSite',
      '@id': 'https://sijilo.com/#website',
      url: 'https://sijilo.com/',
      name: 'Sijilo',
      description: 'Modern Clinic Management Platform',
      publisher: { '@id': 'https://sijilo.com/#organization' }
    },
    {
      '@type': 'Organization',
      '@id': 'https://sijilo.com/#organization',
      name: 'Sijilo',
      url: 'https://sijilo.com/',
      logo: 'https://sijilo.com/sijilo.png'
    }
  ]
}

export default function Landing() {
  const { formatMessage } = useIntl()

  useEffect(() => {
    trackEvent('page_view', { page: 'landing' })
  }, [])

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: 'smooth' })
  }

  const proofPoints = [
    messages.proofRecord,
    messages.proofQueue,
    messages.proofClinical,
    messages.proofBilingual
  ]

  const workflowSteps = [
    [messages.workflowStepOneTitle, messages.workflowStepOneDescription],
    [messages.workflowStepTwoTitle, messages.workflowStepTwoDescription],
    [messages.workflowStepThreeTitle, messages.workflowStepThreeDescription]
  ]

  return (
    <Box
      minH="100vh"
      bg="white"
      color="gray.900"
      sx={{
        fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, sans-serif",
        scrollBehavior: 'smooth',
        '::selection': { background: '#e5e7eb' }
      }}
    >
      <Helmet>
        <title>{formatMessage(messages.metaTitle)}</title>
        <meta name="title" content={formatMessage(messages.metaTitle)} />
        <meta
          name="description"
          content={formatMessage(messages.metaDescription)}
        />
        <meta
          name="keywords"
          content="clinic management, emr, ehr, medical software, doctor dashboard, patient records, health tech, سجلّو, إدارة العيادات, سجلات المرضى, برنامج طبي"
        />
        <meta name="author" content="Sijilo" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sijilo.com/" />
        <meta property="og:title" content={formatMessage(messages.metaTitle)} />
        <meta
          property="og:description"
          content={formatMessage(messages.metaDescription)}
        />
        <meta property="og:image" content="https://sijilo.com/sijilo.png" />
        <meta property="og:locale" content="en_US" />
        <meta property="og:locale:alternate" content="ar_EG" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://sijilo.com/" />
        <meta
          property="twitter:title"
          content={formatMessage(messages.metaTitle)}
        />
        <meta
          property="twitter:description"
          content={formatMessage(messages.metaDescription)}
        />
        <meta
          property="twitter:image"
          content="https://sijilo.com/sijilo.png"
        />
        <link rel="alternate" hrefLang="en" href="https://sijilo.com/" />
        <link rel="alternate" hrefLang="ar" href="https://sijilo.com/" />
        <link rel="alternate" hrefLang="x-default" href="https://sijilo.com/" />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>

      <LandingNavbar
        onFeaturesClick={() => {
          scrollTo('features')
        }}
        onDemoClick={() => {
          scrollTo('book-a-demo')
        }}
      />

      <Box
        as="section"
        pt={{ base: '116px', md: '140px' }}
        pb={{ base: '72px', md: '110px' }}
        px={{ base: 4, md: 6 }}
        bg="#fafafa"
        borderBottom="1px solid"
        borderColor="gray.200"
        backgroundImage="linear-gradient(#ededed 1px, transparent 1px), linear-gradient(90deg, #ededed 1px, transparent 1px)"
        backgroundSize="48px 48px"
      >
        <SimpleGrid
          maxW="1200px"
          mx="auto"
          columns={{ base: 1, lg: 2 }}
          gap={{ base: 14, lg: 16 }}
          alignItems="center"
        >
          <Box maxW="590px">
            <Flex
              display="inline-flex"
              align="center"
              gap={2}
              mb={6}
              px={3}
              py={1.5}
              bg="white"
              border="1px solid"
              borderColor="gray.300"
              borderRadius="full"
              boxShadow="0 1px 2px rgba(0,0,0,.04)"
            >
              <Box w="6px" h="6px" borderRadius="full" bg="gray.900" />
              <Text color="gray.600" fontSize="xs" fontWeight="600">
                {formatMessage(messages.eyebrow)}
              </Text>
            </Flex>

            <Text
              as="h1"
              maxW="570px"
              fontSize={{ base: '42px', md: '58px', xl: '66px' }}
              fontWeight="780"
              letterSpacing="-0.055em"
              lineHeight="0.98"
            >
              {formatMessage(messages.heroTitle)}
            </Text>
            <Text
              mt={7}
              maxW="540px"
              color="gray.600"
              fontSize={{ base: 'md', md: 'lg' }}
              lineHeight="1.7"
            >
              {formatMessage(messages.heroDescription)}
            </Text>

            <Flex mt={9} gap={3} wrap="wrap">
              <Button
                h="44px"
                px={5}
                bg="gray.900"
                color="white"
                borderRadius="10px"
                fontWeight="650"
                rightIcon={<ArrowRightIcon width="16px" />}
                _hover={{ bg: 'black', transform: 'translateY(-1px)' }}
                onClick={() => {
                  trackEvent('cta_click', { label: 'book_a_demo_hero' })
                  scrollTo('book-a-demo')
                }}
              >
                {formatMessage(messages.bookDemo)}
              </Button>
              <Button
                h="44px"
                px={5}
                variant="outline"
                bg="white"
                borderColor="gray.300"
                borderRadius="10px"
                fontWeight="650"
                _hover={{ bg: 'gray.50', borderColor: 'gray.400' }}
                onClick={() => {
                  trackEvent('cta_click', { label: 'see_features' })
                  scrollTo('features')
                }}
              >
                {formatMessage(messages.explorePlatform)}
              </Button>
            </Flex>

            <Flex mt={7} align="center" gap={2} color="gray.500">
              <CheckIcon width="15px" />
              <Text fontSize="sm">{formatMessage(messages.builtFor)}</Text>
            </Flex>
          </Box>

          <ProductPreview />
        </SimpleGrid>
      </Box>

      <Box borderBottom="1px solid" borderColor="gray.200" bg="white">
        <SimpleGrid
          maxW="1200px"
          mx="auto"
          columns={{ base: 2, lg: 4 }}
          px={{ base: 4, md: 6 }}
        >
          {proofPoints.map((point, index) => (
            <Flex
              key={point.id}
              minH={{ base: '76px', md: '88px' }}
              align="center"
              gap={3}
              px={{ base: 2, md: 5 }}
              borderInlineEnd={{
                base: index % 2 === 0 ? '1px solid' : 'none',
                lg: index < 3 ? '1px solid' : 'none'
              }}
              borderBottom={{
                base: index < 2 ? '1px solid' : 'none',
                lg: 'none'
              }}
              borderColor="gray.200"
            >
              <Flex
                w="24px"
                h="24px"
                flexShrink={0}
                align="center"
                justify="center"
                borderRadius="7px"
                bg="gray.100"
              >
                <CheckIcon width="13px" />
              </Flex>
              <Text fontSize={{ base: 'xs', md: 'sm' }} fontWeight="600">
                {formatMessage(point)}
              </Text>
            </Flex>
          ))}
        </SimpleGrid>
      </Box>

      <Box
        as="section"
        id="features"
        py={{ base: '80px', md: '120px' }}
        px={{ base: 4, md: 6 }}
        bg="white"
      >
        <SimpleGrid
          maxW="1200px"
          mx="auto"
          columns={{ base: 1, lg: 3 }}
          gap={{ base: 12, lg: 16 }}
        >
          <Box>
            <Text
              color="gray.500"
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
            >
              {formatMessage(messages.featuresLabel)}
            </Text>
            <Text
              as="h2"
              mt={4}
              fontSize={{ base: '32px', md: '42px' }}
              fontWeight="750"
              letterSpacing="-0.045em"
              lineHeight="1.08"
            >
              {formatMessage(messages.featuresTitle)}
            </Text>
            <Text mt={5} color="gray.600" lineHeight="1.7">
              {formatMessage(messages.featuresDescription)}
            </Text>
          </Box>

          <SimpleGrid
            gridColumn={{ lg: 'span 2' }}
            columns={{ base: 1, md: 2 }}
            gap={0}
          >
            {FEATURES.map((feature, index) => (
              <Box
                key={feature.title.id}
                py={6}
                pe={{ md: index % 2 === 0 ? 7 : 0 }}
                ps={{ md: index % 2 === 1 ? 7 : 0 }}
                borderTop="1px solid"
                borderInlineStart={{
                  md: index % 2 === 1 ? '1px solid' : 'none'
                }}
                borderColor="gray.200"
              >
                <Flex
                  w="38px"
                  h="38px"
                  align="center"
                  justify="center"
                  mb={4}
                  border="1px solid"
                  borderColor="gray.200"
                  borderRadius="9px"
                  bg="gray.50"
                >
                  <Icon as={feature.icon} boxSize="18px" />
                </Flex>
                <Text fontSize="md" fontWeight="700" letterSpacing="-0.015em">
                  {formatMessage(feature.title)}
                </Text>
                <Text mt={2} color="gray.600" fontSize="sm" lineHeight="1.65">
                  {formatMessage(feature.description)}
                </Text>
              </Box>
            ))}
          </SimpleGrid>
        </SimpleGrid>
      </Box>

      <Box
        as="section"
        py={{ base: '80px', md: '120px' }}
        px={{ base: 4, md: 6 }}
        bg="gray.50"
        borderY="1px solid"
        borderColor="gray.200"
      >
        <SimpleGrid
          maxW="1200px"
          mx="auto"
          columns={{ base: 1, lg: 2 }}
          gap={16}
        >
          <Box maxW="500px">
            <Text
              color="gray.500"
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
            >
              {formatMessage(messages.workflowLabel)}
            </Text>
            <Text
              as="h2"
              mt={4}
              fontSize={{ base: '32px', md: '44px' }}
              fontWeight="750"
              letterSpacing="-0.045em"
              lineHeight="1.08"
            >
              {formatMessage(messages.workflowTitle)}
            </Text>
          </Box>

          <Box borderTop="1px solid" borderColor="gray.300">
            {workflowSteps.map(([title, description], index) => (
              <Grid
                key={title.id}
                templateColumns="52px minmax(0, 1fr)"
                gap={4}
                py={6}
                borderBottom="1px solid"
                borderColor="gray.300"
              >
                <Text color="gray.400" fontSize="sm" fontWeight="700">
                  0{index + 1}
                </Text>
                <Box>
                  <Text fontSize="lg" fontWeight="700">
                    {formatMessage(title)}
                  </Text>
                  <Text mt={2} color="gray.600" fontSize="sm" lineHeight="1.7">
                    {formatMessage(description)}
                  </Text>
                </Box>
              </Grid>
            ))}
          </Box>
        </SimpleGrid>
      </Box>

      <Box
        as="section"
        py={{ base: '80px', md: '120px' }}
        px={{ base: 4, md: 6 }}
      >
        <SimpleGrid
          maxW="1200px"
          mx="auto"
          columns={{ base: 1, lg: 2 }}
          gap={{ base: 12, lg: 20 }}
          alignItems="center"
        >
          <Box
            order={{ base: 2, lg: 1 }}
            p={{ base: 5, md: 8 }}
            border="1px solid"
            borderColor="gray.200"
            borderRadius="14px"
            bg="gray.50"
          >
            <Flex justify="space-between" align="center" mb={6}>
              <Box>
                <Text fontSize="xs" color="gray.500">
                  {formatMessage(messages.growthMetric)}
                </Text>
                <Text fontSize="sm" fontWeight="700">
                  {formatMessage(messages.growthOverview)}
                </Text>
              </Box>
              <Badge colorScheme="gray">WHO</Badge>
            </Flex>
            <Box
              h={{ base: '210px', md: '270px' }}
              borderInlineStart="1px solid"
              borderBottom="1px solid"
              borderColor="gray.300"
              backgroundImage="linear-gradient(#e5e7eb 1px, transparent 1px), linear-gradient(90deg, #e5e7eb 1px, transparent 1px)"
              backgroundSize="20% 25%"
            >
              <svg
                width="100%"
                height="100%"
                viewBox="0 0 500 260"
                preserveAspectRatio="none"
              >
                <path
                  d="M0 210 C90 180, 150 190, 230 135 S390 95, 500 30"
                  fill="none"
                  stroke="#d1d5db"
                  strokeWidth="2"
                />
                <path
                  d="M0 230 C90 205, 155 205, 240 165 S400 130, 500 82"
                  fill="none"
                  stroke="#9ca3af"
                  strokeWidth="2"
                />
                <path
                  d="M0 246 C95 225, 175 220, 250 192 S410 165, 500 125"
                  fill="none"
                  stroke="#111827"
                  strokeWidth="3"
                />
                <circle
                  cx="355"
                  cy="167"
                  r="6"
                  fill="#111827"
                  stroke="white"
                  strokeWidth="3"
                />
              </svg>
            </Box>
          </Box>

          <Box order={{ base: 1, lg: 2 }} maxW="500px">
            <Text
              color="gray.500"
              fontSize="xs"
              fontWeight="700"
              letterSpacing="0.1em"
              textTransform="uppercase"
            >
              {formatMessage(messages.growthLabel)}
            </Text>
            <Text
              as="h2"
              mt={4}
              fontSize={{ base: '32px', md: '44px' }}
              fontWeight="750"
              letterSpacing="-0.045em"
              lineHeight="1.08"
            >
              {formatMessage(messages.growthHeading)}
            </Text>
            <Text mt={5} color="gray.600" lineHeight="1.7">
              {formatMessage(messages.growthBody)}
            </Text>
            <Button
              as={Link}
              to="/growth-charts"
              mt={7}
              variant="outline"
              borderColor="gray.300"
              borderRadius="10px"
              rightIcon={<ArrowRightIcon width="15px" />}
            >
              {formatMessage(messages.openGrowthCharts)}
            </Button>
          </Box>
        </SimpleGrid>
      </Box>

      <Box
        as="section"
        id="book-a-demo"
        px={{ base: 4, md: 6 }}
        pb={{ base: 16, md: 24 }}
      >
        <Flex
          maxW="1200px"
          mx="auto"
          direction={{ base: 'column', md: 'row' }}
          align={{ base: 'flex-start', md: 'center' }}
          justify="space-between"
          gap={8}
          px={{ base: 6, md: 10 }}
          py={{ base: 10, md: 12 }}
          borderRadius="16px"
          bg="gray.900"
          color="white"
        >
          <Box maxW="650px">
            <Text
              as="h2"
              fontSize={{ base: '30px', md: '42px' }}
              fontWeight="750"
              letterSpacing="-0.04em"
            >
              {formatMessage(messages.ctaTitle)}
            </Text>
            <Text mt={3} color="whiteAlpha.700" lineHeight="1.7">
              {formatMessage(messages.ctaDescription)}
            </Text>
          </Box>
          <Button
            as="a"
            href={DEMO_URL}
            target="_blank"
            rel="noopener noreferrer"
            flexShrink={0}
            h="44px"
            px={5}
            bg="white"
            color="gray.900"
            borderRadius="10px"
            fontWeight="700"
            rightIcon={<ArrowUpRightIcon width="16px" />}
            _hover={{ bg: 'gray.100', transform: 'translateY(-1px)' }}
            onClick={() => {
              trackEvent('cta_click', { label: 'book_a_demo_cta' })
            }}
          >
            {formatMessage(messages.bookDemo)}
          </Button>
        </Flex>
      </Box>

      <Box as="footer" borderTop="1px solid" borderColor="gray.200" bg="white">
        <Flex
          maxW="1200px"
          mx="auto"
          px={{ base: 4, md: 6 }}
          py={8}
          direction={{ base: 'column', sm: 'row' }}
          align={{ base: 'flex-start', sm: 'center' }}
          justify="space-between"
          gap={3}
        >
          <Box>
            <Text fontSize="sm" fontWeight="700">
              Sijilo
            </Text>
            <Text mt={1} color="gray.500" fontSize="xs">
              {formatMessage(messages.footerTagline)}
            </Text>
          </Box>
          <Text color="gray.500" fontSize="xs">
            {formatMessage(messages.footerRights)}
          </Text>
        </Flex>
      </Box>
    </Box>
  )
}
